import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { UpdateLeague } from "~pages/Leagues/types/upsertLeague.ts";
import type { League, LeagueResponse } from "~types/league.ts";

export const useUpdateLeague = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const request = useApi();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<League, Error, UpdateLeague>({
    mutationFn: async ({ leagueId, ...body }) => {
      const response = await request<LeagueResponse>(`${sport}/o/${organizationId}/leagues/${leagueId}`, {
        method: "PUT",
        body,
      });

      return response?.data?.[0];
    },
    onSuccess: async (updatedLeague, { leagueId }) => {
      if (!updatedLeague?.leagueId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        const existingLeague = updatedScope.resources?.leagues?.[leagueId];

        if (existingLeague && updatedScope.resources.leagues) {
          updatedScope.resources.leagues[leagueId] = {
            ...existingLeague,
            ...updatedLeague,
            leagueId,
          };
        }
      });

      await Promise.all([invalidateCache(), queryClient.invalidateQueries({ queryKey: ["leagues", sport, organizationId] })]);
    },
  });
};
